import React from "react";
import { Thermometer } from "lucide-react";
import { COLOR } from "./pillars/_cards";

const cToF = (c) => Math.round((c * 9) / 5 + 32);

/** Live-temperature impact — today's reading from the car's own sensor and
 *  what it means for the warranty floor trajectory and available SoC. */
export const LiveTempImpactCard = ({ vehicle, climate, floor, soh, vel }) => {
  const liveC = typeof vehicle?.outsideTempC === "number" ? vehicle.outsideTempC : null;
  const seasonalF = climate
    ? Math.round(((climate.summer_high_f ?? 90) + (climate.winter_low_f ?? 40)) / 2)
    : 70;
  const tempF = liveC != null ? cToF(liveC) : seasonalF;
  const tempC = liveC != null ? liveC : ((tempF - 32) * 5) / 9;

  // Calendar-aging multiplier vs the 70°F/21°C ideal (Arrhenius-style).
  const mult = Math.min(2.2, Math.max(0.55, Math.pow(2, (tempC - 21) / 12)));
  const monthsToFloor = vel > 0 ? (soh - floor) / vel : 999;
  const monthsAtTemp = vel > 0 ? (soh - floor) / (vel * mult) : 999;
  const deltaMo = Math.round(Math.min(240, monthsToFloor) - Math.min(240, monthsAtTemp));
  const hot = mult > 1.05;
  const cold = tempF < 50;

  // Cold packs temporarily lock part of the charge until warmed.
  const lockedSoc = cold ? Math.min(15, Math.round((50 - tempF) * 0.4)) : 0;

  const tone = hot ? COLOR.amber : cold ? COLOR.blue : COLOR.green;

  return (
    <div
      className="rounded-xl border p-4"
      style={{ borderColor: `${tone}40`, background: `${tone}08` }}
      data-testid="live-temp-impact-card"
    >
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center border shrink-0"
          style={{ background: `${tone}16`, borderColor: `${tone}55` }}
        >
          <Thermometer className="w-4 h-4" style={{ color: tone }} strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
            Today's temperature {liveC != null ? "· live · your car's sensor" : "· seasonal estimate"}
          </div>
          <div className="text-sm text-white font-semibold leading-snug">
            {hot ? "Heat is working your pack today" : cold ? "Cold pack — some charge naps until warmed" : "Ideal chemistry weather today"}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display text-2xl font-semibold leading-none" style={{ color: tone }} data-testid="live-temp-value">
            {tempF}°<span className="font-mono text-[10px] text-zinc-400">F</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-white/5">
        <div>
          <div className="font-display text-lg text-white font-semibold leading-none" data-testid="live-temp-floor-impact">
            {deltaMo === 0 ? "No change" : `${Math.abs(deltaMo)} mo ${deltaMo > 0 ? "sooner" : "later"}`}
          </div>
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mt-1">
            {floor}% warranty floor · if today's temp were year-round
          </div>
        </div>
        <div>
          <div className="font-display text-lg text-white font-semibold leading-none" data-testid="live-temp-soc-impact">
            {lockedSoc > 0 ? `~${lockedSoc}% locked` : "Full SoC"}
          </div>
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mt-1">
            {lockedSoc > 0 ? "available again once the pack warms" : "no cold-lock on available charge"}
          </div>
        </div>
      </div>

      <p className="font-mono text-[10px] text-zinc-400 leading-relaxed mt-3" data-testid="live-temp-tip">
        {hot
          ? "Heat speeds calendar aging — shade parking and an 80% limit blunt most of it. Your projection below assumes your climate's normal mix, not a single hot day."
          : cold
          ? "Cold slows aging (good) but temporarily hides charge and limits regen. Precondition before driving to unlock the full pack."
          : "Around 70°F the chemistry ages slowest — today neither the warranty clock nor your SoC takes a penalty."}
      </p>
    </div>
  );
};
